import { useState } from "react"

import { HOSTING_PLAN_NAMES } from "@logic/lib/ai-compute/hosting-plan"
import type { UseMutationResult } from "@tanstack/react-query"

import type {
  SolarComputeDataDto,
  SolarComputeOptionDto,
  SolarComputePayoutDto,
  SolarSelectedComputeDto,
} from "~/api/dto/solar-proposal-compute.dto"
import { formatCurrency } from "~/utils/finance/format"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  PricingStepFooter,
  ToggleCard,
  ToggleCardGrid,
} from "../pricing/pricing-step-chrome"
import { usePricingSaveGuard } from "../pricing/use-pricing-save-guard"

/**
 * The AI compute catalog: the offered nodes as zero-or-one toggle cards, the
 * saved node, and one save. Solar's Compute step uses the whole catalog;
 * Battery's Finance Partner & Equipment drawer uses the cards and the draft
 * under its optional equipment. Every figure is the gateway's, formatted only.
 */

export const COMPUTE_TERMS_NOTE = `Vendor owns and maintains the node through its hosting term. Saving puts pricing and savings out of date until they're refreshed.`
/** One card per row on phones; the shared grid's two columns from sm. */
export const COMPUTE_CARD_GRID_CLASS = `grid min-h-0 min-w-0 flex-1 auto-rows-min grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-2`
export const COMPUTE_NO_LONGER_OFFERED = `No longer offered`
export const COMPUTE_SAVED_ON_PROPOSAL = `Saved on this proposal`

const money = (amount: number): string => formatCurrency(amount)

/** "Blackwell Max-Q · 96 GB · 1 GPU", from what PowurOS sent. */
const packageLine = (data: SolarComputeDataDto | null): string | null =>
  data
    ? [
        data.gpuArchitecture,
        `${data.gpuMemoryGb} GB`,
        `${data.gpusPerNode} GPU${data.gpusPerNode === 1 ? `` : `s`}`,
      ]
        .filter(Boolean)
        .join(` · `)
    : null

/**
 * The node's Hosting Agreement Rev 2.10 terms: its Hosting Plan, the
 * guaranteed monthly base and the most the bonus can add. Every figure is the
 * gateway's, which already resolved a node without a stored plan to Max.
 */
const PayoutSummary = ({
  payout,
  testId,
}: {
  payout: SolarComputePayoutDto | undefined
  testId: string
}): JSX.Element => {
  if (
    !payout?.available ||
    payout.plan == null ||
    payout.monthlyBase == null ||
    payout.monthlyBonusCap == null
  ) {
    return (
      <span className="text-[length:var(--step--2)] text-muted">
        Payout unavailable
      </span>
    )
  }
  return (
    <span className="flex flex-col gap-1">
      <span
        className="text-[length:var(--step--2)] font-semibold uppercase tracking-wide text-muted"
        data-testid={`${testId}-plan`}
      >
        {HOSTING_PLAN_NAMES[payout.plan]} Hosting Plan
      </span>
      <span
        className="text-[length:var(--step-0)] font-semibold tabular-nums text-bp-accent"
        data-testid={testId}
      >
        {money(payout.monthlyBase)}
        <span className="text-[length:var(--step--2)] font-normal text-muted">
          /mo base
        </span>
        <span className="text-[length:var(--step--2)] font-normal text-muted">
          {` + up to ${money(payout.monthlyBonusCap)} bonus`}
        </span>
      </span>
    </span>
  )
}

export const ComputeCard = ({
  title,
  data,
  payout,
  hint,
  selected,
  disabled,
  onToggle,
  testIdPrefix,
}: {
  title: string
  data: SolarComputeDataDto | null
  /** Absent from an older gateway's payload. */
  payout: SolarComputePayoutDto | undefined
  hint?: string
  selected: boolean
  disabled: boolean
  onToggle: () => void
  /** `solar-compute` or `battery-compute`: the monthly figure's test id root. */
  testIdPrefix: string
}): JSX.Element => (
  <ToggleCard
    selected={selected}
    disabled={disabled}
    minHeightClass="min-h-36"
    onToggle={onToggle}
    title={
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="font-display text-[length:var(--step-0)] font-semibold text-foreground">
          {title}
        </span>
        {packageLine(data) ? (
          <span className="text-[length:var(--step--2)] text-muted">
            {packageLine(data)}
          </span>
        ) : null}
        {hint ? (
          <span className="text-[length:var(--step--2)] text-bp-gold">
            {hint}
          </span>
        ) : null}
      </span>
    }
  >
    <PayoutSummary payout={payout} testId={`${testIdPrefix}-monthly`} />
  </ToggleCard>
)

const optionTitle = (option: SolarComputeOptionDto): string =>
  option.proposalName ?? option.name

export type ComputeSaveVariables = {
  proposalId: string
  body: { computerId: number | null }
}

/** The saved node's fields the cards show. */
export type ComputeSavedNode = Pick<
  SolarSelectedComputeDto,
  `computerData` | `equipmentId` | `name` | `payout`
>

export type ComputeDraft = {
  /** The chosen node id, or null for none. */
  selectedId: number | null
  changed: boolean
  /** Choosing a node replaces the previous choice, choosing it again clears it. */
  toggle: (id: number) => void
  /**
   * A saved node no longer offered can't be newly chosen: choosing it again
   * only drops the pending removal and keeps the saved node.
   */
  toggleSaved: () => void
  /** Drops the draft, so the saved node is the selection again. */
  clear: () => void
}

/**
 * The zero-or-one node choice over the saved one. With `pickable` false a
 * pending pick reads as no draft (its options no longer apply) while a
 * pending removal stays, since removing needs nothing from PowurOS.
 */
export const useComputeDraft = (
  savedId: number | null,
  { pickable = true }: { pickable?: boolean } = {},
): ComputeDraft => {
  // `undefined` means no draft: the saved node is the selection.
  const [draft, setDraft] = useState<number | null | undefined>(undefined)
  const effective = !pickable && draft !== null ? undefined : draft
  const selectedId = effective === undefined ? savedId : effective
  return {
    selectedId,
    changed: selectedId !== savedId,
    toggle: (id) => setDraft(selectedId === id ? null : id),
    toggleSaved: () => setDraft(selectedId === savedId ? null : undefined),
    clear: () => setDraft(undefined),
  }
}

/**
 * The offered nodes as toggle cards, with a saved node missing from
 * `options` first so it can be removed, or the empty notice.
 */
export const ComputeCardGrid = ({
  options,
  saved,
  savedHint = COMPUTE_NO_LONGER_OFFERED,
  draft,
  disabled,
  onChange,
  emptyNotice,
  testIdPrefix,
}: {
  options: readonly SolarComputeOptionDto[]
  saved: ComputeSavedNode | null
  /** How a saved node missing from `options` is labeled. */
  savedHint?: string
  draft: ComputeDraft
  disabled: boolean
  /** Runs before each toggle, e.g. to clear a failed save. */
  onChange?: () => void
  emptyNotice: string
  testIdPrefix: string
}): JSX.Element => {
  const staleSaved =
    saved != null && !options.some((option) => option.id === saved.equipmentId)
      ? saved
      : null

  if (options.length === 0 && staleSaved == null) {
    return (
      <p
        role="status"
        className="text-[length:var(--step--1)] text-muted"
        data-testid={`${testIdPrefix}-empty`}
      >
        {emptyNotice}
      </p>
    )
  }

  return (
    <ToggleCardGrid
      testId={`${testIdPrefix}-cards`}
      className={COMPUTE_CARD_GRID_CLASS}
    >
      {staleSaved ? (
        <ComputeCard
          title={staleSaved.name}
          data={staleSaved.computerData}
          payout={staleSaved.payout}
          hint={savedHint}
          selected={draft.selectedId === staleSaved.equipmentId}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
          onToggle={() => {
            onChange?.()
            draft.toggleSaved()
          }}
        />
      ) : null}
      {options.map((option) => (
        <ComputeCard
          key={option.id}
          title={optionTitle(option)}
          data={option.computerData}
          payout={option.payout}
          selected={draft.selectedId === option.id}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
          onToggle={() => {
            onChange?.()
            draft.toggle(option.id)
          }}
        />
      ))}
    </ToggleCardGrid>
  )
}

/**
 * The offered nodes, the saved node, and one save. A saved node PowurOS no
 * longer offers still shows, so it can be removed. Save sends the choice once.
 */
export const ComputeCatalog = <TData,>({
  proposalId,
  options,
  saved,
  savedHint = COMPUTE_NO_LONGER_OFFERED,
  onClose,
  update,
  isInvalidError,
  errorMessages,
  emptyNotice,
  testIdPrefix,
}: {
  proposalId: string
  options: readonly SolarComputeOptionDto[]
  saved: SolarSelectedComputeDto | null
  /** How a saved node missing from `options` is labeled. */
  savedHint?: string
  onClose: () => void
  update: UseMutationResult<TData, unknown, ComputeSaveVariables>
  /** PowurOS no longer offers the chosen node: nothing to retry. */
  isInvalidError: (error: unknown) => boolean
  /** The reasons the command gave, when it listed them. */
  errorMessages: (error: unknown) => string[]
  emptyNotice: string
  testIdPrefix: string
}): JSX.Element => {
  const guard = usePricingSaveGuard({ onClose, savedFeedback: false })
  const savedId = saved?.equipmentId ?? null
  const draft = useComputeDraft(savedId)
  const { selectedId, changed } = draft
  const saving = update.isLoading
  const staleSaved =
    saved != null && !options.some((option) => option.id === saved.equipmentId)
      ? saved
      : null

  const submit = (): void => {
    if (saving || !changed || !guard.start()) return
    update.mutate(
      { proposalId, body: { computerId: selectedId } },
      {
        onSettled: guard.release,
        onSuccess: () => {
          draft.clear()
          guard.succeed()
        },
        onError: (error) => {
          // PowurOS no longer offers that node: the refetched options drop it,
          // so the selection goes back to the saved node instead of a card
          // that's gone, and there is nothing to retry.
          if (isInvalidError(error)) {
            draft.clear()
          }
        },
      },
    )
  }

  const errors = update.isError ? errorMessages(update.error) : []
  const retryable = update.isError && !isInvalidError(update.error)

  return (
    <>
      <ComputeCardGrid
        options={options}
        saved={saved}
        savedHint={savedHint}
        draft={draft}
        disabled={saving}
        onChange={() => update.reset()}
        emptyNotice={emptyNotice}
        testIdPrefix={testIdPrefix}
      />
      <p className="mt-4 text-[length:var(--step--2)] text-muted">
        {COMPUTE_TERMS_NOTE}
      </p>
      <PricingStepFooter
        onClose={onClose}
        error={
          update.isError
            ? {
                message:
                  errors.join(` `) ||
                  parseAxiosError(
                    update.error,
                    `Unable to save the AI compute node. Your selection is still here.`,
                  ),
                onRetry: retryable ? submit : undefined,
              }
            : null
        }
        savedMessage={null}
        save={
          options.length > 0 || staleSaved != null
            ? {
                label:
                  selectedId === null && savedId !== null
                    ? `Save without compute`
                    : `Save`,
                saving,
                disabled: saving || !changed,
                onSave: submit,
              }
            : null
        }
      />
    </>
  )
}
